/**
 * Interactive Question Player - ANBIMA Decision Tree Experience
 *
 * Provides step-by-step navigation through interactive questions
 * with chat-style UI, scoring, and branching paths.
 *
 * Based on decision-tree-demo.html design pattern.
 */

import { useState } from 'react';
import {
  XCircle,
  ArrowRight,
  RotateCcw,
  Check,
  X
} from 'lucide-react';
import { formatAlternativeText } from '../utils/textFormatting';

interface InteractivePrompt {
  id: string;
  text: string;
  role?: 'client' | 'system';
  alternatives: {
    letter: string;
    text: string;
    score: number;
    nextPromptId: string | null;
    explanation: string;
  }[];
}

interface InteractiveQuestion {
  question_id: string;
  question_type: 'interactive';
  initialContext: string;
  prompts: InteractivePrompt[];
  optimalPath: string[];
  oeCode?: string;
  difficulty?: string;
  exam_id?: string;
}

interface HistoryStep {
  promptId: string;
  chosenLetter: string;
  chosenText: string;
  score: number;
  isOptimal: boolean;
}

interface InteractiveQuestionPlayerProps {
  question: InteractiveQuestion;
  onComplete?: (score: number, maxScore: number, history: HistoryStep[]) => void;
  onReset?: () => void;
}

export function InteractiveQuestionPlayer({
  question,
  onComplete,
  onReset
}: InteractiveQuestionPlayerProps) {
  const [currentPromptId, setCurrentPromptId] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryStep[]>([]);
  const [selectedAlternative, setSelectedAlternative] = useState<any | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const correctAnswers = history.filter(step => step.score === 5).length;
  const totalQuestions = question.prompts.length;

  // Get current prompt
  const currentPrompt = question.prompts.find(p => p.id === currentPromptId);

  // Handle alternative selection
  const handleSelectAlternative = (alternative: any) => {
    setSelectedAlternative(alternative);
    setShowFeedback(true);
  };

  // Proceed to next prompt
  const handleProceed = () => {
    if (!selectedAlternative || !currentPromptId) return;

    // Add to history
    const isOptimal = question.optimalPath.includes(currentPromptId) && selectedAlternative.score === 5;
    const newStep: HistoryStep = {
      promptId: currentPromptId,
      chosenLetter: selectedAlternative.letter,
      chosenText: selectedAlternative.text,
      score: selectedAlternative.score,
      isOptimal
    };
    setHistory(prev => [...prev, newStep]);

    // Check if we're done
    if (!selectedAlternative.nextPromptId || selectedAlternative.nextPromptId === 'end') {
      setIsCompleted(true);
      const finalHistory = [...history, newStep];
      const finalCorrect = finalHistory.filter(s => s.score === 5).length;
      if (onComplete) {
        onComplete(finalCorrect, totalQuestions, finalHistory);
      }
    } else {
      // Move to next prompt
      setCurrentPromptId(selectedAlternative.nextPromptId);
      setSelectedAlternative(null);
      setShowFeedback(false);
    }
  };

  // Reset the player
  const handleReset = () => {
    setCurrentPromptId(null);
    setHistory([]);
    setSelectedAlternative(null);
    setShowFeedback(false);
    setIsStarted(false);
    setIsCompleted(false);
    if (onReset) onReset();
  };

  // Auto-start on mount
  if (!isStarted && !isCompleted) {
    setIsStarted(true);
    setCurrentPromptId(question.prompts[0]?.id || null);
  }

  // Result Screen
  if (isCompleted) {
    const percentage = (correctAnswers / totalQuestions) * 100;
    let message = 'Precisa melhorar';
    if (percentage === 100) message = 'Perfeito! Todas as respostas corretas.';
    else if (percentage >= 75) message = 'Ótimo desempenho com alguma margem para melhoria.';
    else if (percentage >= 50) message = 'Desempenho razoável - revise os tópicos testados.';

    return (
      <div className="space-y-6">
        {/* Final Score */}
        <div className="text-center animate-fade-in">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Interação Completa</h3>
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-5xl">⭐</span>
            <div className="text-5xl font-extrabold text-gray-900">{correctAnswers}/{totalQuestions}</div>
          </div>
          <p className="text-sm text-gray-600 mb-6">{message}</p>

          {/* Decision Trail */}
          <div className="text-left mb-6">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
              Trilha de Decisões:
            </p>
            <div className="space-y-2">
              {history.map((step, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-3 p-3 rounded-lg text-sm border-2 ${
                    step.score === 5
                      ? 'bg-green-50 text-green-700 border-green-300'
                      : 'bg-red-50 text-red-700 border-red-300'
                  }`}
                >
                  <strong>{idx + 1}.</strong>
                  {step.score === 5 ? (
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" strokeWidth={3} />
                  ) : (
                    <X className="w-4 h-4 text-red-600 flex-shrink-0" strokeWidth={3} />
                  )}
                  <span className="flex-1">{step.chosenText}</span>
                  <span className="font-bold">{step.score === 5 ? '✓' : '✗'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" /> Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Current Prompt Screen
  if (!currentPrompt) {
    return (
      <div className="p-6 bg-red-50 border-2 border-red-300 rounded-lg text-center">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-600">Erro: Prompt "{currentPromptId}" não encontrado</p>
        <button
          onClick={handleReset}
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
        >
          Reiniciar
        </button>
      </div>
    );
  }

  // Helper function to get letter badge color
  const getLetterBadgeColor = (letter: string) => {
    switch (letter.toUpperCase()) {
      case 'A': return 'bg-purple-600';
      case 'B': return 'bg-orange-500';
      case 'C': return 'bg-green-600';
      case 'D': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  // Map difficulty to Portuguese
  const difficultyLabel = question.difficulty === 'easy' ? 'Fácil'
    : question.difficulty === 'medium' ? 'Médio'
    : question.difficulty === 'hard' ? 'Difícil'
    : question.difficulty || 'Médio';

  return (
    <div className="space-y-6">
      {/* Top Badges */}
      <div className="flex items-center gap-2 mb-4">
        <span className="px-3 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">
          {difficultyLabel}
        </span>
        <span className="px-3 py-1 text-xs font-semibold bg-gray-200 text-gray-700 rounded-full">
          {question.question_id}
        </span>
        <span className="px-3 py-1 text-xs font-semibold bg-purple-100 text-purple-700 rounded-full">
          Node: {currentPrompt.id}
        </span>
      </div>

      {/* Subject/Topic */}
      {question.oeCode && (
        <p className="text-sm text-gray-500 mb-3">
          {question.oeCode}
        </p>
      )}

      {/* Initial Context (shown once at start) */}
      {history.length === 0 && question.initialContext && (
        <div className="mb-6">
          <p className="text-base text-gray-800 leading-relaxed">
            {question.initialContext}
          </p>
        </div>
      )}

      {/* Current Prompt */}
      <div className="mb-6 animate-fade-in">
        <p className="text-base text-gray-800 leading-relaxed">
          {currentPrompt.text}
        </p>
      </div>

      {/* Response Options with Circular Badges */}
      <div className="space-y-3 mb-6">
        {currentPrompt.alternatives.map((alt) => {
          const isSelected = selectedAlternative?.letter === alt.letter;
          const isDisabled = showFeedback && !isSelected;
          const isCorrect = alt.score === 5;

          let borderColor = 'border-gray-200';
          let bgColor = 'bg-white';

          if (showFeedback && isSelected) {
            if (isCorrect) {
              borderColor = 'border-green-400';
              bgColor = 'bg-green-50';
            } else {
              borderColor = 'border-red-400';
              bgColor = 'bg-red-50';
            }
          }

          return (
            <button
              key={alt.letter}
              onClick={() => !isDisabled && handleSelectAlternative(alt)}
              disabled={isDisabled}
              className={`w-full text-left p-4 border-2 ${borderColor} ${bgColor} rounded-lg transition-all ${
                isDisabled ? 'opacity-35 cursor-not-allowed' : 'hover:shadow-md cursor-pointer hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {/* Circular Letter Badge */}
                <div className={`w-8 h-8 rounded-full ${getLetterBadgeColor(alt.letter)} flex items-center justify-center flex-shrink-0`}>
                  <span className="text-white font-bold text-sm">{alt.letter}</span>
                </div>

                <div className="flex-1">
                  <span className="text-sm text-gray-800">{formatAlternativeText(alt.text)}</span>
                </div>

                {/* Right/Wrong Indicator */}
                {showFeedback && isSelected && (
                  <div className="flex-shrink-0">
                    {isCorrect ? (
                      <Check className="w-6 h-6 text-green-600" strokeWidth={3} />
                    ) : (
                      <X className="w-6 h-6 text-red-600" strokeWidth={3} />
                    )}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Explanation Box (shown after selection) */}
      {showFeedback && selectedAlternative && selectedAlternative.explanation && (
        <div className="mb-6 p-4 bg-orange-50 border-l-4 border-orange-400 rounded-lg animate-fade-in">
          <p className="text-sm font-semibold text-orange-900 mb-2">
            {selectedAlternative.score === 5 ? '✓' : '×'} Resposta correta: {currentPrompt.alternatives.find(a => a.score === 5)?.letter || 'N/A'}
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            {selectedAlternative.explanation}
          </p>
        </div>
      )}

      {/* Proceed Button */}
      {showFeedback && selectedAlternative && (
        <button
          onClick={handleProceed}
          className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 animate-fade-in"
        >
          Próximo <ArrowRight className="w-5 h-5" />
        </button>
      )}

      {/* Bottom Score Indicator */}
      <div className="flex items-center justify-center gap-2 text-sm text-gray-600 pt-4 border-t border-gray-200">
        <span>⭐</span>
        <span>Pontuação: <strong className="text-gray-900">{correctAnswers}/{totalQuestions}</strong></span>
      </div>
    </div>
  );
}
